import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../lib/api';

/**
 * ReviewPromptCard — the App Store review prompt's satisfaction gate.
 *
 * Renders a `type: 'review_prompt'` notification ("How's Autonoma going
 * so far? 👍 👎") in the bell panel and on the Dashboard:
 *
 *   👍  → POST /api/review-prompt/respond {up}   → opens the Shopify App
 *         Store write-review modal in a new tab, then confirms
 *         store_opened. If the browser blocked the popup the link is
 *         shown inline — the flow can never dead-end.
 *   👎  → POST /api/review-prompt/respond {down} → the in-app /feedback
 *         form (routes to the support inbox). Never the store link.
 *   Not now → POST /api/review-prompt/respond {dismissed} → retried at
 *         the next success moment 30+ days later (max 3 attempts).
 *
 * Every transition is recorded server-side (review_prompt.* events,
 * hashed shop id). `onLocalUpdate(id, patch)` keeps the bell's local
 * list in sync without a second PUT that would double-count.
 */
export default function ReviewPromptCard({ notif, variant = 'bell', onLocalUpdate, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // 'up' | 'down' | 'dismissed'
  const [outcome, setOutcome] = useState(null); // 'up' | 'down' | 'dismissed' | 'blocked'
  const [blockedUrl, setBlockedUrl] = useState(null);
  const [error, setError] = useState(null);

  const rp = notif.reviewPrompt || {};
  const reviewUrl = rp.reviewUrl || 'https://apps.shopify.com/autonoma-ai#modal-show=WriteReviewModal';
  const feedbackRoute = `${rp.feedbackRoute || '/feedback?from=review_prompt'}&n=${encodeURIComponent(notif.id)}`;

  const respond = async (response) => {
    if (busy) return;
    setBusy(response);
    setError(null);
    try {
      const res = await apiPost('/api/review-prompt/respond', { notificationId: notif.id, response });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      if (response === 'dismissed') {
        onLocalUpdate?.(notif.id, { read: true });
        setOutcome('dismissed');
        return;
      }

      onLocalUpdate?.(notif.id, { read: true, actioned: true });

      if (response === 'down') {
        setOutcome('down');
        onClose?.();
        navigate(data.feedbackRoute || feedbackRoute);
        return;
      }

      // 👍 — the App Store write-review modal, in a new tab.
      const url = data.reviewUrl || reviewUrl;
      const win = window.open(url, '_blank', 'noopener');
      if (win) {
        setOutcome('up');
        apiPost('/api/review-prompt/event', { event: 'store_opened', notificationId: notif.id }).catch(() => {});
      } else {
        // Popup blocked: show the link so a click still gets there.
        setOutcome('blocked');
        setBlockedUrl(url);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
    } finally {
      setBusy(null);
    }
  };

  const onBlockedLinkClick = () => {
    setOutcome('up');
    apiPost('/api/review-prompt/event', { event: 'store_opened', notificationId: notif.id }).catch(() => {});
  };

  const compact = variant === 'bell';
  const btnBase = compact
    ? { padding: '6px 12px', fontSize: 13, borderRadius: 8, fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer', border: '1px solid #d9dbe3', background: 'white', fontWeight: 600 }
    : { padding: '10px 18px', fontSize: 14, borderRadius: 8, fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer', border: '1px solid #d9dbe3', background: 'white', fontWeight: 600 };

  const answered = outcome && outcome !== 'blocked';
  const alreadyAnswered = notif.actioned && !outcome;

  return (
    <div
      className={compact ? 'au-review-prompt au-review-prompt--bell' : 'au-review-prompt au-review-prompt--card'}
      data-testid="review-prompt-card"
      role="group"
      aria-label="How's Autonoma going so far?"
    >
      <div className={compact ? 'au-notif-item__title' : 'au-suggestion__title'}>{notif.title}</div>
      <div className={compact ? 'au-notif-item__msg' : 'au-suggestion__msg'}>{notif.message}</div>

      {alreadyAnswered && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#6d7175' }}>Thanks for your answer.</div>
      )}

      {!answered && !alreadyAnswered && (
        <div style={{ display: 'flex', gap: 8, marginTop: compact ? 8 : 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            style={btnBase}
            className="au-review-prompt__thumb"
            onClick={() => respond('up')}
            disabled={!!busy}
            aria-label="Thumbs up — going well"
            data-testid="review-thumbs-up"
          >
            {'\uD83D\uDC4D'} Going well
          </button>
          <button
            type="button"
            style={btnBase}
            className="au-review-prompt__thumb"
            onClick={() => respond('down')}
            disabled={!!busy}
            aria-label="Thumbs down — not great"
            data-testid="review-thumbs-down"
          >
            {'\uD83D\uDC4E'} Not great
          </button>
          <button
            type="button"
            style={{ ...btnBase, border: 'none', background: 'transparent', color: '#6d7175', fontWeight: 500 }}
            onClick={() => respond('dismissed')}
            disabled={!!busy}
            data-testid="review-not-now"
          >
            Not now
          </button>
        </div>
      )}

      {outcome === 'blocked' && blockedUrl && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          Thanks! Your browser blocked the new tab —{' '}
          <a href={blockedUrl} target="_blank" rel="noopener noreferrer" onClick={onBlockedLinkClick}>
            open the App Store review page
          </a>.
        </div>
      )}
      {outcome === 'up' && (
        <div className="au-notif-item__done" style={{ marginTop: 8 }} data-testid="review-thanks-up">
          {'\u2705'} Thank you! The Shopify App Store review page is open in a new tab.
        </div>
      )}
      {outcome === 'down' && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#6d7175' }}>Thanks — tell us what to fix.</div>
      )}
      {outcome === 'dismissed' && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#6d7175' }}>No problem — we won&rsquo;t ask again for a while.</div>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#d82c0d' }}>{error}</div>
      )}
    </div>
  );
}
